import { useEffect, useState } from "react";
import AnalyticsFilters from "../AnalyticsFilters";
import AnalyticsTable from "../AnalyticsTable";
import { fetchUserJourney, fetchUserJourneyDetail } from "../../../services/api";
import UserJourneyDrawer from "../UserJourneyDrawer";
import { fetchJourneyFilterOptions } from "../../../services/api";
import ExportButtons from "../../ExportButtons";
import { handleExportResponse } from "../../../utils/exportUtils";
import { exportUserJourney } from "../../../services/api";
import PageSizeSelector from "../../PageSizeSelector";

const deviceStyles = {
    desktop: "bg-sky-50 text-sky-700",
    mobile: "bg-violet-50 text-violet-700",
    tablet: "bg-amber-50 text-amber-700",
};
const Pill = ({ children, className = "" }) => (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${className}`}>
        {children}
    </span>
);

const UserJourneyTab = () => {
    const [selectedVisitor, setSelectedVisitor] = useState(null);
    const [journey, setJourney] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const defaultFilters = {
        search: "",
        utm_source: "",
        utm_medium: "",
        utm_campaign: "",
        browser: "",
        device: "",
        user_type: "",
        converted: "",
        operating_system: "",
        language: "",
        gateway: "",
        landing_path: "",
        referrer: "",
        start_date: "",
        end_date: "",
        journey_stage: "",
    };
    const [filters, setFilters] = useState(defaultFilters);
    const [filterOptions, setFilterOptions] = useState({
        sources: [],
        mediums: [],
        campaigns: [],
        browsers: [],
        devices: [],
        user_types: [],
        products: [],
        payment_gateways: [],
        languages: [],
        operating_systems: [],
    });
    const openJourney = async (row) => {
        const response = await fetchUserJourneyDetail(row.id);
        if (response.status === 1) {
            setJourney(response.data);
            setSelectedVisitor(row);
            setDrawerOpen(true);
        }
    };
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [pagination, setPagination] = useState({
        count: 0,
        total_pages: 1,
        page_size: 10,
    });

    useEffect(() => {
        loadData();
    }, [page, pageSize, filters]);

    useEffect(() => {
        loadFilterOptions();
    }, []);

    const loadFilterOptions = async () => {
        const response = await fetchJourneyFilterOptions();
        if (response.status === 1) {
            setFilterOptions(response.data);
        }
    };

    const loadData = async () => {
        try {
            setLoading(true);
            const response = await fetchUserJourney({
                page,
                page_size: pageSize,
                ...filters,
            });
            if (response.status === 1) {
                setRows(response.results);
                setPagination({
                    count: response.count,
                    total_pages: response.total_pages,
                    page_size: response.page_size,
                });
                setPageSize(response.page_size);
            }
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (key, value) => {
        setPage(1);
        setFilters((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const handlePageSizeChange = (e) => {
        setPage(1);
        setPageSize(Number(e.target.value));
    };

    const resetFilters = () => {
        setPage(1);
        setFilters(defaultFilters);
    };

    const columns = [
        {
            header: "Visitor",
            render: (row) => (
                <div>
                    <div className="text-sm font-medium text-slate-900">
                        {row.visitor_uuid.slice(0, 8)}...
                    </div>
                    <div className="text-[11px] text-slate-400">#{row.id}</div>
                </div>
            ),
        },
        {
            header: "User",
            render: (row) => (
                <div>
                    <div className="text-sm text-slate-700">{row.user_email || "Guest"}</div>
                    {row.user_type && (
                        <Pill className="mt-0.5 bg-slate-100 text-slate-500"> {row.user_type === "user" ? "Artist" : row.user_type}</Pill>
                    )}
                </div>
            ),
        },
        {
            header: "Campaign",
            render: (row) => (
                <span className="text-sm text-slate-600">{row.utm_campaign || "direct"}</span>
            ),
        },
        {
            header: "Sessions",
            render: (row) => (
                <span className="text-sm font-semibold tabular-nums text-slate-900">
                    {row.total_sessions}
                </span>
            ),
        },
        {
            header: "Last Event",
            accessor: "last_event",
        },
        {
            header: "Converted",
            render: (row) =>
                row.converted ? (
                    <Pill className="bg-emerald-50 text-emerald-700">Yes</Pill>
                ) : (
                    <Pill className="bg-slate-100 text-slate-400">No</Pill>
                ),
        },
        {
            header: "Last Seen",
            render: (row) => (
                <span className="text-xs text-slate-500">
                    {row.last_seen ? new Date(row.last_seen).toLocaleString() : "-"}
                </span>
            ),
        },
    ];

    const handleExport = async (format) => {

        try {
            const params = {
                ...filters,
                export: format,
            };
            Object.keys(params).forEach(key => {
                if (
                    params[key] === "" ||
                    params[key] === null ||
                    params[key] === undefined
                ) {
                    delete params[key];
                }
            });
            const response = await exportUserJourney(params);
            handleExportResponse(
                response,
                format,
                `user-journeys.${format}`
            );

        } catch (error) {
            console.error(error);
            alert("Failed to export user journeys.");
        }
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <PageSizeSelector
                    pageSize={pageSize}
                    onPageSizeChange={handlePageSizeChange}
                    options={[10, 20, 50, 100]}
                    label="Items Per Page:"
                />

                <ExportButtons
                    onExport={handleExport}
                    isLoading={loading}
                />
            </div>
            <AnalyticsFilters
                filters={filters}
                filterOptions={filterOptions}
                onChange={handleFilterChange}
                onReset={resetFilters}
            />

            {loading ? (
                <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-slate-200 bg-white py-16">
                    <div className="h-8 w-8 animate-spin rounded-full border-2 border-indigo-600 border-t-transparent" />
                    <p className="text-sm text-gray-500">Loading journeys...</p>
                </div>
            ) : (
                <AnalyticsTable
                    columns={columns}
                    data={rows}
                    currentPage={page}
                    totalPages={pagination.total_pages}
                    totalCount={pagination.count}
                    pageSize={pagination.page_size}
                    onPageSizeChange={handlePageSizeChange}
                    onPageChange={setPage}
                    onRowClick={openJourney}
                />
            )}
            <UserJourneyDrawer
                open={drawerOpen}
                journey={journey}
                onClose={() => setDrawerOpen(false)}
            />
        </div>
    );
};

export default UserJourneyTab;